import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Coffee, Building2, Store, ArrowRight, Sparkles, HeartHandshake, Users, ClipboardList, UserRound, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/d0d54f8c-6f6e-4c78-b7c4-299776d77a72/images/2c8718cd5549651e8d8664ae305355d1b131da347b2fd970a490d8ab5a4c48f2.jpeg";
const CROISSANT = "https://images.unsplash.com/photo-1613187869034-fa8e42b2ab1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const ROOM = "https://images.unsplash.com/photo-1754597302822-4b96f3442d3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const ADVANTAGES = [
  { icon: Sparkles, title: "Più servizi per gli ospiti", desc: "Colazioni di qualità al bar o in camera, senza pensieri per la struttura." },
  { icon: HeartHandshake, title: "Più valore per la struttura", desc: "Un servizio in più che aumenta la soddisfazione e le recensioni." },
  { icon: Users, title: "Nuovi clienti per i locali", desc: "Bar e pasticcerie raggiungono gli ospiti dei B&B della zona." },
  { icon: ClipboardList, title: "Ordini semplici e organizzati", desc: "Tutto gestito da un'unica piattaforma, chiaro e ordinato." },
];

const STEPS = [
  { n: "1", title: "Il B&B e i locali si registrano", desc: "Strutture, bar e pasticcerie entrano nella rete MorningBox della loro zona." },
  { n: "2", title: "L'ospite o il B&B crea l'ordine", desc: "Direttamente dal pannello del B&B o dagli ospiti tramite QR code in camera." },
  { n: "3", title: "Il bar prepara la colazione", desc: "Disponibile per il ritiro al bar oppure per la consegna in camera." },
];

export default function Home() {
  const navigate = useNavigate();
  const [prelaunch, setPrelaunch] = useState(true);
  const [guestOpen, setGuestOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", city: "", phone: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setPrelaunch(data.prelaunch_mode)).catch(() => {});
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/leads", lead);
      toast.success(data.message);
      setGuestOpen(false);
      setLead({ name: "", email: "", city: "", phone: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div>
      {/* Prelaunch banner */}
      {prelaunch && (
        <div className="bg-navy text-white" data-testid="prelaunch-banner">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-sm text-center">
            <Bell className="w-4 h-4 text-orange shrink-0" />
            <span>Stiamo selezionando i primi partner MorningBox nella tua zona.</span>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-orange bg-orange/10 px-4 py-1.5 rounded-full">
              <Coffee className="w-3.5 h-3.5" /> Colazioni per gli ospiti
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-navy">
              La colazione perfetta,<br /><span className="text-orange">dove vuoi tu.</span>
            </h1>
            <p className="mt-6 text-lg text-navy/70 max-w-xl leading-relaxed">
              MorningBox mette in contatto B&B, bar e pasticcerie per offrire agli ospiti colazioni di qualità, al bar oppure direttamente in camera.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-how-btn" onClick={() => navigate("/come-funziona")} className="rounded-full bg-navy hover:bg-navy-700 text-white h-12 px-6 text-base">
                Scopri come funziona <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button data-testid="hero-register-bnb-btn" onClick={() => navigate("/registra/bnb")} variant="outline" className="rounded-full border-navy/20 text-navy h-12 px-6 text-base hover:bg-navy/5">
                Registra la tua struttura
              </Button>
              <Button data-testid="hero-register-vendor-btn" onClick={() => navigate("/registra/locale")} variant="outline" className="rounded-full border-navy/20 text-navy h-12 px-6 text-base hover:bg-navy/5">
                Registra il tuo locale
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <img src={HERO_IMG} alt="Colazione MorningBox" className="rounded-3xl brand-shadow w-full h-[420px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 brand-shadow hidden sm:flex items-center gap-3">
              <img src={CROISSANT} alt="Cornetto" className="w-14 h-14 rounded-xl object-cover" />
              <div>
                <p className="text-sm font-semibold text-navy">Colazione fresca ogni giorno</p>
                <p className="text-xs text-navy/50">Classica · Speciale · Gourmet</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Prelaunch CTA cards */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="bg-navy rounded-3xl p-8 lg:p-12 text-white">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold">MorningBox sta arrivando nella tua zona</h2>
            <p className="mt-3 text-white/70 leading-relaxed">
              Stiamo selezionando B&B, bar e pasticcerie interessati a entrare nella prima rete locale dedicata alle colazioni per gli ospiti.
            </p>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <button onClick={() => navigate("/registra/bnb")} data-testid="cta-register-bnb" className="group text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-colors">
              <Building2 className="w-8 h-8 text-orange" />
              <p className="mt-4 font-semibold text-lg">Registra il tuo B&B</p>
              <p className="text-sm text-white/60 mt-1">Offri colazioni di qualità ai tuoi ospiti.</p>
              <span className="mt-4 inline-flex items-center text-orange text-sm font-medium">Inizia <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
            </button>
            <button onClick={() => navigate("/registra/locale")} data-testid="cta-register-vendor" className="group text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-colors">
              <Store className="w-8 h-8 text-orange" />
              <p className="mt-4 font-semibold text-lg">Registra il tuo bar o pasticceria</p>
              <p className="text-sm text-white/60 mt-1">Raggiungi nuovi clienti nella tua città.</p>
              <span className="mt-4 inline-flex items-center text-orange text-sm font-medium">Inizia <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
            </button>
            <button onClick={() => setGuestOpen(true)} data-testid="cta-guest" className="group text-left bg-orange hover:bg-orange-600 rounded-2xl p-6 transition-colors">
              <UserRound className="w-8 h-8 text-white" />
              <p className="mt-4 font-semibold text-lg">Sono un ospite</p>
              <p className="text-sm text-white/80 mt-1">Vuoi MorningBox nella tua struttura?</p>
              <span className="mt-4 inline-flex items-center text-white text-sm font-medium">Lascia il contatto <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
            </button>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange">I vantaggi</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-navy tracking-tight">Un servizio che conviene a tutti</h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ADVANTAGES.map((a, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white border border-navy/10 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-cream flex items-center justify-center">
                <a.icon className="w-6 h-6 text-orange" />
              </div>
              <p className="mt-4 font-semibold text-navy">{a.title}</p>
              <p className="mt-1.5 text-sm text-navy/60 leading-relaxed">{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Come funziona */}
      <section className="bg-cream mt-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <img src={ROOM} alt="Camera B&B" className="rounded-3xl brand-shadow w-full h-[400px] object-cover order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange">Come funziona</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-navy tracking-tight">In tre semplici passaggi</h2>
            <div className="mt-8 space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4">
                  <div className="w-11 h-11 rounded-full bg-navy text-white font-heading font-bold flex items-center justify-center shrink-0">{s.n}</div>
                  <div>
                    <p className="font-semibold text-navy">{s.title}</p>
                    <p className="text-sm text-navy/60 mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guest lead dialog */}
      <Dialog open={guestOpen} onOpenChange={setGuestOpen}>
        <DialogContent className="rounded-2xl" data-testid="guest-lead-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy font-heading text-xl">Sono un ospite</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-navy/70 leading-relaxed">
            Il servizio MorningBox non è ancora disponibile nella tua struttura. Lascia il tuo contatto per essere avvisato al lancio.
          </p>
          <form onSubmit={submitLead} className="space-y-3 mt-2">
            <div><Label>Nome</Label><Input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="rounded-xl mt-1" data-testid="lead-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="rounded-xl mt-1" data-testid="lead-email" /></div>
            <div><Label>Città</Label><Input required value={lead.city} onChange={(e) => setLead({ ...lead, city: e.target.value })} className="rounded-xl mt-1" data-testid="lead-city" /></div>
            <div><Label>Telefono (facoltativo)</Label><Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="rounded-xl mt-1" data-testid="lead-phone" /></div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-orange hover:bg-orange-600 text-white h-11" data-testid="lead-submit">
              {sending ? "Invio..." : "Avvisami al lancio"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
